import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Store, TrendingUp, TrendingDown, MessageSquare, X, ThumbsUp, ThumbsDown, Minus, MapPin, Calendar, Search, ChevronDown } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { KPICard } from '../components/ui/KPICard';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
// Available markets - exported for use in other components
export const markets = [{
  id: 'berlin-mitte',
  name: 'LIDL Berlin-Mitte',
  region: 'Deutschland',
  rating: 4.3
}, {
  id: 'muenchen-zentrum',
  name: 'LIDL München-Zentrum',
  region: 'Deutschland',
  rating: 4.6
}, {
  id: 'paris-marais',
  name: 'LIDL Paris-Marais',
  region: 'Frankreich',
  rating: 3.8
}, {
  id: 'lyon-confluence',
  name: 'LIDL Lyon-Confluence',
  region: 'Frankreich',
  rating: 4.1
}, {
  id: 'madrid-centro',
  name: 'LIDL Madrid-Centro',
  region: 'Spanien',
  rating: 3.9
}, {
  id: 'barcelona-eixample',
  name: 'LIDL Barcelona-Eixample',
  region: 'Spanien',
  rating: 4.2
}, {
  id: 'rome-trastevere',
  name: 'LIDL Roma-Trastevere',
  region: 'Italien',
  rating: 4.5
}, {
  id: 'milan-navigli',
  name: 'LIDL Milano-Navigli',
  region: 'Italien',
  rating: 4.4
}, {
  id: 'london-camden',
  name: 'LIDL London-Camden',
  region: 'Großbritannien',
  rating: 4.0
}, {
  id: 'warsaw-centrum',
  name: 'LIDL Warszawa-Centrum',
  region: 'Polen',
  rating: 4.3
}];
// Mock data per market
const marketData: Record<string, {
  positive: number;
  neutral: number;
  negative: number;
  total: number;
  avgRating: number;
  trend: number;
}> = {
  'berlin-mitte': {
    positive: 245,
    neutral: 82,
    negative: 38,
    total: 365,
    avgRating: 4.3,
    trend: 0.12
  },
  'paris-marais': {
    positive: 156,
    neutral: 98,
    negative: 76,
    total: 330,
    avgRating: 3.8,
    trend: -0.08
  },
  'muenchen-zentrum': {
    positive: 312,
    neutral: 65,
    negative: 23,
    total: 400,
    avgRating: 4.6,
    trend: 0.18
  },
  'lyon-confluence': {
    positive: 278,
    neutral: 72,
    negative: 30,
    total: 380,
    avgRating: 4.1,
    trend: 0.1
  },
  'madrid-centro': {
    positive: 168,
    neutral: 95,
    negative: 57,
    total: 320,
    avgRating: 3.9,
    trend: -0.05
  },
  'barcelona-eixample': {
    positive: 245,
    neutral: 78,
    negative: 42,
    total: 365,
    avgRating: 4.2,
    trend: 0.08
  },
  'rome-trastevere': {
    positive: 298,
    neutral: 68,
    negative: 24,
    total: 390,
    avgRating: 4.5,
    trend: 0.22
  },
  'milan-navigli': {
    positive: 285,
    neutral: 75,
    negative: 40,
    total: 400,
    avgRating: 4.4,
    trend: 0.12
  },
  'london-camden': {
    positive: 210,
    neutral: 95,
    negative: 55,
    total: 360,
    avgRating: 4.0,
    trend: 0.05
  },
  'warsaw-centrum': {
    positive: 268,
    neutral: 72,
    negative: 35,
    total: 375,
    avgRating: 4.3,
    trend: 0.13
  }
};
// Topic clusters data per market
const marketTopicClusters: Record<string, Array<{
  name: string;
  size: number;
  sentiment: 'positive' | 'neutral' | 'negative';
  comments: Array<{
    text: string;
    date: string;
    rating: number;
  }>;
}>> = {
  'berlin-mitte': [{
    name: 'Frische & Qualität',
    size: 85,
    sentiment: 'positive',
    comments: [{
      text: 'Die Obst- und Gemüseabteilung ist immer top frisch!',
      date: '2024-01-28',
      rating: 5
    }, {
      text: 'Beste Fleischtheke in der Umgebung.',
      date: '2024-01-27',
      rating: 5
    }]
  }, {
    name: 'Freundlichkeit',
    size: 62,
    sentiment: 'positive',
    comments: [{
      text: 'Personal ist immer freundlich und hilfsbereit.',
      date: '2024-01-28',
      rating: 5
    }]
  }, {
    name: 'Wartezeiten',
    size: 45,
    sentiment: 'negative',
    comments: [{
      text: 'Zu wenig Kassen offen, lange Wartezeiten.',
      date: '2024-01-26',
      rating: 2
    }]
  }, {
    name: 'Sortiment',
    size: 38,
    sentiment: 'positive',
    comments: []
  }, {
    name: 'Preise',
    size: 32,
    sentiment: 'neutral',
    comments: []
  }, {
    name: 'Parkplatz',
    size: 28,
    sentiment: 'negative',
    comments: []
  }],
  'paris-marais': [{
    name: 'Wartezeiten',
    size: 72,
    sentiment: 'negative',
    comments: [{
      text: 'Samstags ist es unmöglich hier einzukaufen.',
      date: '2024-01-27',
      rating: 1
    }]
  }, {
    name: 'Sortiment',
    size: 58,
    sentiment: 'positive',
    comments: []
  }, {
    name: 'Preise',
    size: 45,
    sentiment: 'neutral',
    comments: []
  }, {
    name: 'Sauberkeit',
    size: 38,
    sentiment: 'negative',
    comments: []
  }, {
    name: 'Freundlichkeit',
    size: 35,
    sentiment: 'neutral',
    comments: []
  }],
  'muenchen-zentrum': [{
    name: 'Frische & Qualität',
    size: 95,
    sentiment: 'positive',
    comments: [{
      text: 'Beste Fleischtheke in der Umgebung, sehr gute Beratung.',
      date: '2024-01-27',
      rating: 5
    }]
  }, {
    name: 'Freundlichkeit',
    size: 78,
    sentiment: 'positive',
    comments: []
  }, {
    name: 'Sortiment',
    size: 65,
    sentiment: 'positive',
    comments: []
  }, {
    name: 'Sauberkeit',
    size: 42,
    sentiment: 'positive',
    comments: []
  }, {
    name: 'Preise',
    size: 35,
    sentiment: 'neutral',
    comments: []
  }]
};
// Default topic clusters for markets without specific data
const defaultTopicClusters = [{
  name: 'Frische & Qualität',
  size: 65,
  sentiment: 'positive' as const,
  comments: []
}, {
  name: 'Freundlichkeit',
  size: 52,
  sentiment: 'positive' as const,
  comments: []
}, {
  name: 'Wartezeiten',
  size: 38,
  sentiment: 'negative' as const,
  comments: []
}, {
  name: 'Sortiment',
  size: 45,
  sentiment: 'positive' as const,
  comments: []
}, {
  name: 'Preise',
  size: 32,
  sentiment: 'neutral' as const,
  comments: []
}];
// 12-month trend data (generic, scaled per market)
const generateTrendData = (avgRating: number, trend: number, posBase: number, neuBase: number, negBase: number) => {
  const months = ['Feb 23', 'Mär 23', 'Apr 23', 'Mai 23', 'Jun 23', 'Jul 23', 'Aug 23', 'Sep 23', 'Okt 23', 'Nov 23', 'Dez 23', 'Jan 24'];
  const baseRating = avgRating - trend * 6;
  // Generate sentiment percentages that shift gradually over time
  const posStart = posBase - 4;
  const negStart = negBase + 3;
  return months.map((month, i) => {
    const posPercent = Math.round(posStart + (posBase - posStart) * (i / 11));
    const negPercent = Math.round(negStart + (negBase - negStart) * (i / 11));
    const neuPercent = 100 - posPercent - negPercent;
    return {
      month,
      rating: Math.round((baseRating + trend * i / 2 + (Math.random() * 0.1 - 0.05)) * 100) / 100,
      reviews: Math.floor(25 + Math.random() * 15),
      positivePercent: posPercent,
      neutralPercent: neuPercent,
      negativePercent: negPercent
    };
  });
};
// Simple custom treemap component
const SimpleTreemap = ({
  data,
  onClick
}: {
  data: Array<{
    name: string;
    size: number;
    sentiment: string;
  }>;
  onClick: (item: any) => void;
}) => {
  const total = data.reduce((sum, item) => sum + item.size, 0);
  const sortedData = [...data].sort((a, b) => b.size - a.size);
  const getColors = (sentiment: string) => {
    switch (sentiment) {
      case 'positive':
        return {
          bg: '#d4edda',
          border: '#639754',
          text: '#1a3d1a'
        };
      case 'negative':
        return {
          bg: '#f8d7da',
          border: '#E03C32',
          text: '#3d0a0a'
        };
      default:
        return {
          bg: '#fff3cd',
          border: '#FFD301',
          text: '#3d3200'
        };
    }
  };
  return <div className="grid grid-cols-4 gap-2 h-full" data-id="element-751">
      {sortedData.map((item, index) => {
      const colors = getColors(item.sentiment);
      let colSpan = 1,
        rowSpan = 1;
      if (index === 0) {
        colSpan = 2;
        rowSpan = 2;
      } else if (index === 1 || index === 2) {
        colSpan = 2;
        rowSpan = 1;
      }
      return <div key={item.name} onClick={() => onClick(item)} className="rounded-lg p-3 flex flex-col justify-center items-center text-center cursor-pointer transition-opacity hover:opacity-80" style={{
        backgroundColor: colors.bg,
        border: `2px solid ${colors.border}`,
        gridColumn: `span ${colSpan}`,
        gridRow: `span ${rowSpan}`
      }} data-id="element-752">
            <span className="font-semibold text-sm leading-tight" style={{
          color: colors.text
        }} data-id="element-753">
              {item.name}
            </span>
            <span className="text-xs mt-1 opacity-70" style={{
          color: colors.text
        }} data-id="element-754">
              {item.size.toLocaleString('de-DE')}
            </span>
          </div>;
    })}
    </div>;
};
interface MarktStimmungPageProps {
  selectedMarket: string | null;
  onMarketChange: (marketId: string | null) => void;
}
export function MarktStimmungPage({
  selectedMarket,
  onMarketChange
}: MarktStimmungPageProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedTopic, setSelectedTopic] = useState<any>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const filteredMarkets = markets.filter(m => m.name.toLowerCase().includes(searchQuery.toLowerCase()) || m.region.toLowerCase().includes(searchQuery.toLowerCase()));
  const selectedMarketInfo = markets.find(m => m.id === selectedMarket);
  const currentData = selectedMarket ? marketData[selectedMarket] || marketData['berlin-mitte'] : null;
  const currentTopics = selectedMarket ? marketTopicClusters[selectedMarket] || defaultTopicClusters : [];
  const currentTrend = selectedMarket && currentData ? generateTrendData(currentData.avgRating, currentData.trend, Math.round(currentData.positive / currentData.total * 100), Math.round(currentData.neutral / currentData.total * 100), Math.round(currentData.negative / currentData.total * 100)) : [];
  const totalSentiments = currentData ? currentData.positive + currentData.neutral + currentData.negative : 0;
  const positivePercent = currentData ? Math.round(currentData.positive / totalSentiments * 100) : 0;
  const neutralPercent = currentData ? Math.round(currentData.neutral / totalSentiments * 100) : 0;
  const negativePercent = currentData ? Math.round(currentData.negative / totalSentiments * 100) : 0;
  const [selectedSentimentFilter, setSelectedSentimentFilter] = useState<'positive' | 'neutral' | 'negative' | null>(null);
  const handleTopicClick = (topic: any, sentiment?: 'positive' | 'neutral' | 'negative') => {
    const fullTopic = currentTopics.find(t => t.name === topic.name);
    if (fullTopic && fullTopic.comments && fullTopic.comments.length > 0) {
      setSelectedTopic(fullTopic);
      setSelectedSentimentFilter(sentiment || null);
      setIsDialogOpen(true);
    }
  };
  const getFilteredComments = () => {
    if (!selectedTopic) return [];
    if (!selectedSentimentFilter) return selectedTopic.comments;
    return selectedTopic.comments.filter((c: any) => {
      if (selectedSentimentFilter === 'positive') return c.rating >= 4;
      if (selectedSentimentFilter === 'negative') return c.rating <= 2;
      return c.rating === 3;
    });
  };
  const sentimentLabels: Record<string, string> = {
    positive: 'Positiv',
    neutral: 'Neutral',
    negative: 'Negativ'
  };
  const sentimentColors: Record<string, string> = {
    positive: '#639754',
    neutral: '#b39500',
    negative: '#E60A0A'
  };
  // If no market selected, show market selection
  if (!selectedMarket) {
    return <div className="p-6 space-y-6 overflow-y-auto h-screen" data-id="element-755">
        <Card className="!p-6" data-id="element-756">
          <div className="text-center mb-6" data-id="element-757">
            <div className="w-16 h-16 rounded-2xl bg-lidl-blue/10 flex items-center justify-center mx-auto mb-4" data-id="element-758">
              <Store size={32} className="text-lidl-blue" data-id="element-759" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-760">
              Markt auswählen
            </h2>
            <p className="text-gray-500 text-sm" data-id="element-761">
              Wählen Sie einen Markt aus, um das Stimmungsbarometer anzuzeigen
            </p>
          </div>

          {/* Search */}
          <div className="relative mb-4" data-id="element-762">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" data-id="element-763" />
            <input type="text" placeholder="Markt suchen..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-lidl-blue/20 focus:border-lidl-blue" data-id="element-764" />
          </div>

          {/* Market List */}
          <div className="space-y-2 max-h-96 overflow-y-auto" data-id="element-765">
            {filteredMarkets.map(market => <button key={market.id} onClick={() => onMarketChange(market.id)} className="w-full flex items-center justify-between p-4 rounded-lg border border-gray-200 hover:border-lidl-blue hover:bg-lidl-blue/5 transition-all text-left" data-id="element-766">
                <div className="flex items-center gap-3" data-id="element-767">
                  <div className="w-10 h-10 rounded-full bg-lidl-blue/10 flex items-center justify-center" data-id="element-768">
                    <Store size={18} className="text-lidl-blue" data-id="element-769" />
                  </div>
                  <div data-id="element-770">
                    <div className="font-medium text-gray-900" data-id="element-771">
                      {market.name}
                    </div>
                    <div className="text-xs text-gray-500" data-id="element-772">{market.region}</div>
                  </div>
                </div>
                <div className="text-right" data-id="element-773">
                  <div className="flex items-center gap-1" data-id="element-774">
                    {[...Array(5)].map((_, i) => <span key={i} className={`text-sm ${i < Math.floor(market.rating) ? 'text-yellow-400' : 'text-gray-200'}`} data-id="element-775">
                        ★
                      </span>)}
                  </div>
                  <div className="text-xs text-gray-500" data-id="element-776">
                    {market.rating.toFixed(1)} Sterne
                  </div>
                </div>
              </button>)}
          </div>
        </Card>
      </div>;
  }
  return <div className="p-6 space-y-6 overflow-y-auto h-screen" data-id="element-777">
      {/* Market Selector */}
      <Card className="!p-4 relative z-50" data-id="element-778">
        <div className="flex items-center gap-4 flex-wrap" data-id="element-779">
          <div className="flex items-center gap-2 text-sm font-medium text-gray-700" data-id="element-780">
            <Store size={16} data-id="element-781" />
            <span data-id="element-782">Markt:</span>
          </div>

          <div className="relative flex-1 max-w-md" data-id="element-783">
            <button onClick={() => setIsDropdownOpen(!isDropdownOpen)} className="w-full flex items-center justify-between px-4 py-2.5 bg-lidl-blue text-white rounded-lg shadow-md" data-id="element-784">
              <div className="flex items-center gap-2" data-id="element-785">
                <Store size={16} data-id="element-786" />
                <span className="font-medium" data-id="element-787">{selectedMarketInfo?.name}</span>
              </div>
              <ChevronDown size={16} className={`transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} data-id="element-788" />
            </button>

            {isDropdownOpen && <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-lg shadow-xl border border-gray-200 z-[100] max-h-64 overflow-y-auto" data-id="element-789">
                <div className="p-2" data-id="element-790">
                  <input type="text" placeholder="Suchen..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-lidl-blue/20" data-id="element-791" />
                </div>
                {filteredMarkets.map(market => <button key={market.id} onClick={() => {
              onMarketChange(market.id);
              setIsDropdownOpen(false);
              setSearchQuery('');
            }} className={`w-full flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 text-left ${market.id === selectedMarket ? 'bg-lidl-blue/5' : ''}`} data-id="element-792">
                    <Store size={14} className="text-gray-400" data-id="element-793" />
                    <div className="flex-1" data-id="element-794">
                      <div className="text-sm font-medium text-gray-900" data-id="element-795">
                        {market.name}
                      </div>
                      <div className="text-xs text-gray-500" data-id="element-796">
                        {market.region}
                      </div>
                    </div>
                    <div className="text-xs text-gray-500" data-id="element-797">
                      {market.rating.toFixed(1)} ★
                    </div>
                  </button>)}
              </div>}
          </div>

          <button onClick={() => onMarketChange(null)} className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-798">
            Markt wechseln
          </button>
        </div>
      </Card>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-799">
        <KPICard title="Durchschnittliche Bewertung" value={currentData?.avgRating.toFixed(1) || '0'} icon={<TrendingUp size={18} data-id="element-801" />} data-id="element-800" />
        <KPICard title="Gesamtbewertungen" value={currentData?.total.toLocaleString('de-DE') || '0'} icon={<MessageSquare size={18} data-id="element-803" />} data-id="element-802" />
        <KPICard title="Positive Resonanz" value={`${positivePercent}%`} icon={<ThumbsUp size={18} data-id="element-805" />} data-id="element-804" />
        <KPICard title="Negative Resonanz" value={`${negativePercent}%`} icon={<ThumbsDown size={18} data-id="element-807" />} data-id="element-806" />
      </div>

      <Card data-id="element-808">
        <div className="p-6" data-id="element-809">
          <h3 className="text-lg font-semibold text-gray-900 mb-1" data-id="element-810">
            Absolute Resonanz
          </h3>
          <p className="text-sm text-gray-500 mb-6" data-id="element-811">
            Verteilung der Sentiment-Analyse
          </p>
          <div className="mb-6" data-id="element-812">
            <div className="h-12 rounded-lg overflow-hidden flex" data-id="element-813">
              <div style={{
              width: `${positivePercent}%`,
              backgroundColor: '#639754'
            }} className="flex items-center justify-center" data-id="element-814">
                {positivePercent > 10 && <span className="text-white text-sm font-semibold" data-id="element-815">
                    {positivePercent}%
                  </span>}
              </div>
              <div style={{
              width: `${neutralPercent}%`,
              backgroundColor: '#FFD301'
            }} className="flex items-center justify-center" data-id="element-816">
                {neutralPercent > 10 && <span className="text-gray-800 text-sm font-semibold" data-id="element-817">
                    {neutralPercent}%
                  </span>}
              </div>
              <div style={{
              width: `${negativePercent}%`,
              backgroundColor: '#E03C32'
            }} className="flex items-center justify-center" data-id="element-818">
                {negativePercent > 10 && <span className="text-white text-sm font-semibold" data-id="element-819">
                    {negativePercent}%
                  </span>}
              </div>
            </div>
          </div>

          {/* Legend with numbers */}
          <div className="space-y-3" data-id="element-820">
            <div className="flex items-center justify-between p-3 rounded-lg" style={{
            backgroundColor: '#e8f0e5'
          }} data-id="element-821">
              <div className="flex items-center gap-3" data-id="element-822">
                <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{
                backgroundColor: '#639754'
              }} data-id="element-823">
                  <ThumbsUp size={18} className="text-white" data-id="element-824" />
                </div>
                <div data-id="element-825">
                  <div className="font-semibold text-gray-900" data-id="element-826">Positiv</div>
                  <div className="text-xs text-gray-500" data-id="element-827">Zufriedene Kunden</div>
                </div>
              </div>
              <div className="text-right" data-id="element-828">
                <div className="text-2xl font-bold" style={{
                color: '#639754'
              }} data-id="element-829">
                  {currentData?.positive.toLocaleString('de-DE')}
                </div>
                <div className="text-xs text-gray-500" data-id="element-830">
                  {positivePercent}% aller Bewertungen
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between p-3 rounded-lg" style={{
            backgroundColor: '#fff9db'
          }} data-id="element-831">
              <div className="flex items-center gap-3" data-id="element-832">
                <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{
                backgroundColor: '#FFD301'
              }} data-id="element-833">
                  <Minus size={18} className="text-gray-800" data-id="element-834" />
                </div>
                <div data-id="element-835">
                  <div className="font-semibold text-gray-900" data-id="element-836">Neutral</div>
                  <div className="text-xs text-gray-500" data-id="element-837">
                    Gemischte Meinungen
                  </div>
                </div>
              </div>
              <div className="text-right" data-id="element-838">
                <div className="text-2xl font-bold" style={{
                color: '#b39500'
              }} data-id="element-839">
                  {currentData?.neutral.toLocaleString('de-DE')}
                </div>
                <div className="text-xs text-gray-500" data-id="element-840">
                  {neutralPercent}% aller Bewertungen
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between p-3 rounded-lg" style={{
            backgroundColor: '#fce8e7'
          }} data-id="element-841">
              <div className="flex items-center gap-3" data-id="element-842">
                <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{
                backgroundColor: '#E03C32'
              }} data-id="element-843">
                  <ThumbsDown size={18} className="text-white" data-id="element-844" />
                </div>
                <div data-id="element-845">
                  <div className="font-semibold text-gray-900" data-id="element-846">Negativ</div>
                  <div className="text-xs text-gray-500" data-id="element-847">
                    Unzufriedene Kunden
                  </div>
                </div>
              </div>
              <div className="text-right" data-id="element-848">
                <div className="text-2xl font-bold" style={{
                color: '#E03C32'
              }} data-id="element-849">
                  {currentData?.negative.toLocaleString('de-DE')}
                </div>
                <div className="text-xs text-gray-500" data-id="element-850">
                  {negativePercent}% aller Bewertungen
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-851">
          {currentTopics.map((topic, index) => {
          let posPct, neuPct, negPct;
          if (topic.sentiment === 'positive') {
            posPct = 0.68;
            neuPct = 0.12;
            negPct = 0.2;
          } else if (topic.sentiment === 'negative') {
            posPct = 0.2;
            neuPct = 0.1;
            negPct = 0.7;
          } else {
            posPct = 0.3;
            neuPct = 0.4;
            negPct = 0.3;
          }
          const positiveCount = Math.round(topic.size * posPct);
          const neutralCount = Math.round(topic.size * neuPct);
          const negativeCount = topic.size - positiveCount - neutralCount;
          return <Card key={index} className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => handleTopicClick(topic)} data-id="element-852">
                <div className="p-4" data-id="element-853">
                  <div className="flex items-center justify-between mb-3" data-id="element-854">
                    <span className="text-sm text-gray-600" data-id="element-855">
                      Erwähnung:{' '}
                      <span className="font-semibold text-gray-900" data-id="element-856">
                        {topic.name}
                      </span>
                    </span>
                    <span className="text-sm font-semibold text-gray-900" data-id="element-857">
                      {topic.size.toLocaleString('de-DE')}
                    </span>
                  </div>

                  <div className="w-full h-3 flex rounded-full overflow-hidden mb-2" data-id="element-858">
                    <div onClick={e => {
                  e.stopPropagation();
                  handleTopicClick(topic, 'positive');
                }} className="cursor-pointer hover:opacity-80 transition-opacity" title="Positive Kommentare anzeigen" style={{
                  width: `${posPct * 100}%`,
                  backgroundColor: '#639754'
                }} data-id="element-859" />
                    <div onClick={e => {
                  e.stopPropagation();
                  handleTopicClick(topic, 'neutral');
                }} className="cursor-pointer hover:opacity-80 transition-opacity" title="Neutrale Kommentare anzeigen" style={{
                  width: `${neuPct * 100}%`,
                  backgroundColor: '#FFF000'
                }} data-id="element-860" />
                    <div onClick={e => {
                  e.stopPropagation();
                  handleTopicClick(topic, 'negative');
                }} className="cursor-pointer hover:opacity-80 transition-opacity" title="Negative Kommentare anzeigen" style={{
                  width: `${negPct * 100}%`,
                  backgroundColor: '#E60A0A'
                }} data-id="element-861" />
                  </div>

                  <div className="flex items-center justify-between text-xs text-gray-600 mt-2" data-id="element-862">
                    <div className="flex items-center gap-1.5" data-id="element-863">
                      <div className="w-2 h-2 rounded-full bg-[#639754]" data-id="element-864" />
                      {positiveCount.toLocaleString('de-DE')}
                    </div>
                    <div className="flex items-center gap-1.5" data-id="element-865">
                      <div className="w-2 h-2 rounded-full bg-[#FFF000]" data-id="element-866" />
                      {neutralCount.toLocaleString('de-DE')}
                    </div>
                    <div className="flex items-center gap-1.5" data-id="element-867">
                      <div className="w-2 h-2 rounded-full bg-[#E60A0A]" data-id="element-868" />
                      {negativeCount.toLocaleString('de-DE')}
                    </div>
                  </div>
                </div>
              </Card>;
        })}
        </div>
        {/* 12-Month Trend Chart */}
        <Card data-id="element-869">
          <div className="p-6" data-id="element-870">
            <div className="flex items-center justify-between mb-6" data-id="element-871">
              <div data-id="element-872">
                <h3 className="text-lg font-semibold text-gray-900" data-id="element-873">
                  Reputationsentwicklung
                </h3>
                <p className="text-sm text-gray-500" data-id="element-874">
                  Sentiment-Verteilung der letzten 12 Monate (in %)
                </p>
              </div>
              <div className="flex items-center gap-5 text-sm" data-id="element-875">
                <div className="flex items-center gap-2" data-id="element-876">
                  <div className="w-3 h-3 rounded-full" style={{
                  backgroundColor: '#639754'
                }} data-id="element-877"></div>
                  <span className="text-gray-600" data-id="element-878">Positiv</span>
                </div>
                <div className="flex items-center gap-2" data-id="element-879">
                  <div className="w-3 h-3 rounded-full" style={{
                  backgroundColor: '#FFD301'
                }} data-id="element-880"></div>
                  <span className="text-gray-600" data-id="element-881">Neutral</span>
                </div>
                <div className="flex items-center gap-2" data-id="element-882">
                  <div className="w-3 h-3 rounded-full" style={{
                  backgroundColor: '#E03C32'
                }} data-id="element-883"></div>
                  <span className="text-gray-600" data-id="element-884">Negativ</span>
                </div>
              </div>
            </div>

            <div className="h-72" data-id="element-885">
              <ResponsiveContainer width="100%" height="100%" data-id="element-886">
                <LineChart data={currentTrend} data-id="element-887">
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} data-id="element-888" />
                  <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{
                  fontSize: 11,
                  fill: '#9ca3af'
                }} data-id="element-889" />
                  <YAxis domain={[0, 100]} axisLine={false} tickLine={false} tick={{
                  fontSize: 11,
                  fill: '#9ca3af'
                }} tickFormatter={value => `${value}%`} data-id="element-890" />
                  <Tooltip contentStyle={{
                  borderRadius: '8px',
                  border: 'none',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                  fontSize: '12px'
                }} formatter={(value: number, name: string) => {
                  const labels: Record<string, string> = {
                    positivePercent: 'Positiv',
                    neutralPercent: 'Neutral',
                    negativePercent: 'Negativ'
                  };
                  return [`${value}%`, labels[name] || name];
                }} data-id="element-891" />
                  <Line type="monotone" dataKey="positivePercent" stroke="#639754" strokeWidth={3} dot={{
                  fill: '#639754',
                  strokeWidth: 2,
                  r: 4
                }} activeDot={{
                  r: 6,
                  fill: '#639754',
                  stroke: '#fff',
                  strokeWidth: 2
                }} isAnimationActive={false} data-id="element-892" />
                  <Line type="monotone" dataKey="neutralPercent" stroke="#FFD301" strokeWidth={3} dot={{
                  fill: '#FFD301',
                  strokeWidth: 2,
                  r: 4
                }} activeDot={{
                  r: 6,
                  fill: '#FFD301',
                  stroke: '#fff',
                  strokeWidth: 2
                }} isAnimationActive={false} data-id="element-893" />
                  <Line type="monotone" dataKey="negativePercent" stroke="#E03C32" strokeWidth={3} dot={{
                  fill: '#E03C32',
                  strokeWidth: 2,
                  r: 4
                }} activeDot={{
                  r: 6,
                  fill: '#E03C32',
                  stroke: '#fff',
                  strokeWidth: 2
                }} isAnimationActive={false} data-id="element-894" />
                </LineChart>
              </ResponsiveContainer>
            </div>

            {/* Trend Summary */}
            {currentTrend.length > 1 && <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between flex-wrap gap-4" data-id="element-895">
                <div className="flex items-center gap-6" data-id="element-896">
                  <div className="flex items-center gap-2" data-id="element-897">
                    <TrendingUp size={16} style={{
                  color: '#639754'
                }} data-id="element-898" />
                    <span className="text-sm text-gray-600" data-id="element-899">
                      Positiv:{' '}
                      <span className="font-semibold" style={{
                    color: '#639754'
                  }} data-id="element-900">
                        {currentTrend[currentTrend.length - 1].positivePercent}%
                      </span>
                    </span>
                  </div>
                  <div className="flex items-center gap-2" data-id="element-901">
                    <Minus size={16} style={{
                  color: '#b39500'
                }} data-id="element-902" />
                    <span className="text-sm text-gray-600" data-id="element-903">
                      Neutral:{' '}
                      <span className="font-semibold" style={{
                    color: '#b39500'
                  }} data-id="element-904">
                        {currentTrend[currentTrend.length - 1].neutralPercent}%
                      </span>
                    </span>
                  </div>
                  <div className="flex items-center gap-2" data-id="element-905">
                    <TrendingDown size={16} style={{
                  color: '#E03C32'
                }} data-id="element-906" />
                    <span className="text-sm text-gray-600" data-id="element-907">
                      Negativ:{' '}
                      <span className="font-semibold" style={{
                    color: '#E03C32'
                  }} data-id="element-908">
                        {currentTrend[currentTrend.length - 1].negativePercent}%
                      </span>
                    </span>
                  </div>
                </div>
                <div className="text-sm text-gray-500" data-id="element-909">
                  Veränderung Positiv:{' '}
                  <span className="font-semibold" style={{
                color: '#639754'
              }} data-id="element-910">
                    +
                    {currentTrend[currentTrend.length - 1].positivePercent - currentTrend[0].positivePercent}{' '}
                    Pp.
                  </span>
                </div>
              </div>}
          </div>
        </Card>
        {/* Topic Detail Dialog */}
        <AnimatePresence data-id="element-911">
          {isDialogOpen && selectedTopic && <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="fixed inset-0 bg-black/50 flex items-center justify-center z-[600] p-4" onClick={() => setIsDialogOpen(false)} data-id="element-912">
              <motion.div initial={{
            scale: 0.95,
            opacity: 0
          }} animate={{
            scale: 1,
            opacity: 1
          }} exit={{
            scale: 0.95,
            opacity: 0
          }} className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden" onClick={e => e.stopPropagation()} data-id="element-913">
                {/* Dialog Header */}
                <div className="p-6 border-b border-gray-200 bg-gray-50" data-id="element-914">
                  <div className="flex items-center justify-between" data-id="element-915">
                    <div data-id="element-916">
                      <div className="flex items-center gap-3" data-id="element-917">
                        <h3 className="text-lg font-semibold text-gray-900" data-id="element-918">
                          {selectedTopic.name}
                        </h3>
                        {selectedSentimentFilter ? <Badge variant={selectedSentimentFilter === 'positive' ? 'success' : selectedSentimentFilter === 'negative' ? 'danger' : 'warning'} data-id="element-919">
                            {sentimentLabels[selectedSentimentFilter]}
                          </Badge> : <Badge variant={selectedTopic.sentiment === 'positive' ? 'success' : selectedTopic.sentiment === 'negative' ? 'danger' : 'warning'} data-id="element-920">
                            {sentimentLabels[selectedTopic.sentiment]}
                          </Badge>}
                      </div>
                      <p className="text-sm text-gray-500 mt-1" data-id="element-921">
                        {selectedTopic.size.toLocaleString('de-DE')} Erwähnungen
                        in Kundenbewertungen
                      </p>
                      {selectedSentimentFilter && <div className="flex gap-2 mt-3" data-id="element-922">
                          {(['positive', 'neutral', 'negative'] as const).map(s => <button key={s} onClick={() => setSelectedSentimentFilter(s)} className={`px-3 py-1 rounded-full text-xs font-medium transition-all ${selectedSentimentFilter === s ? 'text-white shadow-sm' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} style={selectedSentimentFilter === s ? {
                      backgroundColor: sentimentColors[s]
                    } : undefined} data-id="element-923">
                                {sentimentLabels[s]}
                              </button>)}
                        </div>}
                    </div>
                    <button onClick={() => setIsDialogOpen(false)} className="p-2 hover:bg-gray-200 rounded-full transition-colors" data-id="element-924">
                      <X size={20} data-id="element-925" />
                    </button>
                  </div>
                </div>

                {/* Dialog Content */}
                <div className="p-6 overflow-y-auto max-h-[60vh]" data-id="element-926">
                  <div className="space-y-4" data-id="element-927">
                    {getFilteredComments().length === 0 && <div className="text-center py-8 text-gray-400" data-id="element-928">
                        <MessageSquare size={32} className="mx-auto mb-3 opacity-50" data-id="element-929" />
                        <p className="text-sm" data-id="element-930">
                          Keine Kommentare für{' '}
                          <span className="font-medium" style={{
                      color: sentimentColors[selectedSentimentFilter || 'neutral']
                    }} data-id="element-931">
                            {sentimentLabels[selectedSentimentFilter || 'neutral']}
                          </span>{' '}
                          verfügbar.
                        </p>
                      </div>}
                    {getFilteredComments().map((comment: any, index: number) => <motion.div key={index} initial={{
                  opacity: 0,
                  y: 10
                }} animate={{
                  opacity: 1,
                  y: 0
                }} transition={{
                  delay: index * 0.05
                }} className="p-4 bg-gray-50 rounded-lg border border-gray-100" data-id="element-932">
                          <div className="flex items-start justify-between gap-4 mb-2" data-id="element-933">
                            <div className="flex items-center gap-2" data-id="element-934">
                              <MapPin size={14} className="text-lidl-blue" data-id="element-935" />
                              <span className="text-sm font-medium text-gray-900" data-id="element-936">
                                {selectedMarketInfo?.name}
                              </span>
                            </div>
                            <div className="flex items-center gap-2" data-id="element-937">
                              <div className="flex" data-id="element-938">
                                {[...Array(5)].map((_, i) => <span key={i} className={`text-sm ${i < comment.rating ? 'text-yellow-400' : 'text-gray-200'}`} data-id="element-939">
                                    ★
                                  </span>)}
                              </div>
                              <span className="text-xs text-gray-400 flex items-center gap-1" data-id="element-940">
                                <Calendar size={12} data-id="element-941" />
                                {new Date(comment.date).toLocaleDateString('de-DE')}
                              </span>
                            </div>
                          </div>
                          <p className="text-sm text-gray-700 italic" data-id="element-942">
                            "{comment.text}"
                          </p>
                        </motion.div>)}
                  </div>
                </div>

                {/* Dialog Footer */}
                <div className="p-4 border-t border-gray-200 bg-gray-50 flex justify-end" data-id="element-943">
                  <Button variant="primary" onClick={() => setIsDialogOpen(false)} data-id="element-944">
                    Schließen
                  </Button>
                </div>
              </motion.div>
            </motion.div>}
        </AnimatePresence>
      </Card>
    </div>;
}